import { useMutation, useQuery } from "@apollo/client";
import { Box, Skeleton } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useFeatureFlags } from "hooks/useFeatureFlags";

import {
  ATTACH_SHIPMENT_BILL_OF_LADING_MUTATION,
  BILL_OF_LADING_LINK_REFETCH_QUERIES,
} from "../Shipments/api";
import { SHIPPING_DOCUMENT_SHIPMENT_LINK_QUERY, SHIPPING_PAPERWORK_QUERY } from "./api";
import { CandidateShipmentList, LinkedShipmentSummary } from "./ManifestShipmentSections";
import { SectionCard } from "./sections/SectionCard";
import { SectionHeader } from "./sections/SectionHeader";

/**
 * The shipment side of a bill of lading: the containers whose waste it covered,
 * or — when nothing is attached — the open shipments it might belong to.
 *
 * The same card `ManifestLinkedShipmentCard` renders, over the other link
 * column, and it reuses that file's two section components rather than copying
 * them. The one real difference is that this side has no auto-matcher behind
 * it: a bill of lading reaches a shipment only when a person links it here or
 * picks it from the shipment page's document picker.
 */
export function ShippingDocumentLinkedShipmentCard({
  shippingDocumentId,
  tenantId,
}: {
  shippingDocumentId: string;
  tenantId: string | undefined;
}) {
  const alerts = useAlerts();
  const { featureFlags } = useFeatureFlags();
  const enabled = !!featureFlags[FeatureFlagKey.ContainerTracking];

  const { data, loading } = useQuery(SHIPPING_DOCUMENT_SHIPMENT_LINK_QUERY, {
    variables: { id: shippingDocumentId },
    skip: !enabled,
  });

  const [attach, { loading: attaching }] = useMutation(ATTACH_SHIPMENT_BILL_OF_LADING_MUTATION, {
    // The paperwork list's Linked shipment column sits behind the same flag as
    // this card, so anyone who can link here can watch that cell go stale.
    refetchQueries: [
      ...BILL_OF_LADING_LINK_REFETCH_QUERIES,
      SHIPPING_PAPERWORK_QUERY,
      "ShippingDocumentShipmentLink",
    ],
  });

  if (!enabled) {
    return null;
  }

  const document = data?.wasteShippingDocument;
  const linkedShipment = document?.linkedShipment;
  const containers = document?.linkedContainers ?? [];
  const candidates = document?.candidateShipments ?? [];

  if (loading && !document) {
    return (
      <Box>
        <SectionCard>
          <SectionHeader title="Shipment & containers" />
          <Skeleton variant="rounded" height={80} />
        </SectionCard>
      </Box>
    );
  }

  // Nothing attached and nothing plausible to offer — the same rule the manifest
  // card follows. A facility that packages its shipments elsewhere would
  // otherwise get an empty container-tracking card on every bill of lading.
  if (!linkedShipment && candidates.length === 0) {
    return null;
  }

  const handleLink = async (shipmentId: string) => {
    try {
      await attach({ variables: { input: { shipmentId, shippingDocumentId } } });
      alerts.success("Bill of lading linked to shipment.");
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to link the shipment.");
    }
  };

  return (
    <Box>
      <SectionCard>
        <SectionHeader title="Shipment & containers" />
        {linkedShipment ? (
          <LinkedShipmentSummary
            shipment={linkedShipment}
            containers={containers}
            tenantId={tenantId}
          />
        ) : (
          <CandidateShipmentList
            candidates={candidates}
            tenantId={tenantId}
            attaching={attaching}
            onLink={handleLink}
            paperwork="billOfLading"
          />
        )}
      </SectionCard>
    </Box>
  );
}
